import React, { useRef, useCallback, useEffect } from 'react';

interface UseAcceleratingHoldOptions {
  onAction: () => void;
  disabled?: boolean;
  initialDelay?: number;
}

/**
 * Hook that enables accelerating repeat behavior when holding down a button.
 * - Single click / tap: strictly 1-step action (guaranteed never double-fires).
 * - Long press / hold: starts repeating after initialDelay (480ms) and smoothly accelerates.
 */
export function useAcceleratingHold({
  onAction,
  disabled = false,
  initialDelay = 480,
}: UseAcceleratingHoldOptions) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const lastTickRef = useRef<number>(0);
  const isHoldingRef = useRef<boolean>(false);
  const actionRef = useRef(onAction);
  const lastPointerActionTimestamp = useRef<number>(0);

  useEffect(() => {
    actionRef.current = onAction;
  }, [onAction]);

  const stop = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    isHoldingRef.current = false;
  }, []);

  useEffect(() => {
    return () => stop();
  }, [stop]);

  const loop = useCallback(
    (now: number) => {
      if (!isHoldingRef.current) return;

      const elapsed = now - startTimeRef.current;

      // Accelerating interval based on elapsed holding time:
      // Starts comfortably at 180ms, accelerates down gently so it never runs away uncontrollably
      let interval = 180;
      if (elapsed > 4000) {
        interval = 25;
      } else if (elapsed > 2500) {
        interval = 45;
      } else if (elapsed > 1400) {
        interval = 75;
      } else if (elapsed > 600) {
        interval = 120;
      } else {
        interval = 175;
      }

      if (now - lastTickRef.current >= interval) {
        actionRef.current();
        lastTickRef.current = now;
      }

      animFrameRef.current = requestAnimationFrame(loop);
    },
    []
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled || e.button !== 0) return;
      stop();

      lastPointerActionTimestamp.current = Date.now();
      // Execute the first step immediately on down
      actionRef.current();
      isHoldingRef.current = true;

      // Start hold timer only after initialDelay (e.g. 480ms)
      timeoutRef.current = setTimeout(() => {
        if (!isHoldingRef.current) return;
        startTimeRef.current = performance.now();
        lastTickRef.current = performance.now();
        animFrameRef.current = requestAnimationFrame(loop);
      }, initialDelay);
    },
    [disabled, initialDelay, loop, stop]
  );

  const onPointerUp = useCallback(() => {
    stop();
  }, [stop]);

  const onPointerLeave = useCallback(() => {
    stop();
  }, [stop]);

  const onPointerCancel = useCallback(() => {
    stop();
  }, [stop]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      // Prevent synthetic clicks within 600ms of pointerdown from double-incrementing
      const timeSincePointer = Date.now() - lastPointerActionTimestamp.current;
      if (timeSincePointer < 600) {
        e.preventDefault();
        return;
      }
      // If triggered by keyboard (Enter or Space) where no pointerdown occurred
      if (!disabled) {
        actionRef.current();
      }
    },
    [disabled]
  );

  const onContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  return {
    onPointerDown,
    onPointerUp,
    onPointerLeave,
    onPointerCancel,
    onClick,
    onContextMenu,
  };
}
